import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { subscribeToNotifications } from '../services/firebaseService';

const Layout = ({ children }) => {
  const [unreadNotificationCount, setUnreadNotificationCount] = useState(0);
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser, logout, isAdmin } = useAuth();

  useEffect(() => {
    if (!currentUser) return;

    const unsubscribeNotifications = subscribeToNotifications(currentUser.uid, (notificationsData) => {
      const unreadCount = notificationsData.filter(notification => !notification.read).length;
      setUnreadNotificationCount(unreadCount);
    });

    return () => {
      unsubscribeNotifications();
    };
  }, [currentUser]);

  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);

  const handleLogout = async () => {
    setShowLogoutConfirm(true);
  };

  const confirmLogout = async () => {
    try {
      await logout();
      navigate('/');
    } catch (error) {
      console.error('Error logging out:', error);
    }
    setShowLogoutConfirm(false);
  };

  const cancelLogout = () => {
    setShowLogoutConfirm(false);
  };

  const isActive = (path) => {
    if (path === '/dashboard') {
      return location.pathname === '/dashboard' && !location.search.includes('tab=notifications');
    }
    return location.pathname.startsWith(path);
  };

  const isNotificationsActive = () => {
    return location.pathname === '/dashboard' && location.search.includes('tab=notifications');
  };

  return (
    <div className="flex h-screen bg-gray-50">
      {/* Fixed Sidebar */}
      <div className="w-64 bg-white shadow-sm border-r border-gray-200 fixed h-full z-10">
        <div className="p-3">
          <div className="flex flex-col items-start">
            <img
              src="/Goyalco_logo.png"
              alt="Goyalco Logo"
              className="h-14 w-auto"
              onError={(e) => {
                e.target.style.display = 'none';
              }}
            />
            <h1 className="text-xl font-semibold text-gray-900">
              Liasoning IQ
            </h1>
          </div>
        </div>
        
        <nav className="mt-3">
          <div className="px-6 mb-4">
            <h2 className="text-sm font-medium text-gray-500 uppercase tracking-wider">
              Discover
            </h2>
          </div>
          
          <div className="space-y-1">
            <button 
              onClick={() => navigate('/dashboard')}
              className={`flex items-center px-6 py-2 text-sm w-full text-left ${
                isActive('/dashboard')
                  ? 'text-gray-900 bg-gray-100 border-r-2 border-blue-500' 
                  : 'text-gray-700 hover:bg-gray-50'
              }`}
            >
              <svg className="mr-3 h-5 w-5 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 9a2 2 0 00-2 2m0 0V5a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2m-2-4h.01M17 16h.01" />
              </svg>
              Projects
            </button>

            <button 
              onClick={() => navigate('/dashboard?tab=notifications')}
              className={`flex items-center justify-between px-6 py-2 text-sm w-full text-left ${
                isNotificationsActive()
                  ? 'text-gray-900 bg-gray-100 border-r-2 border-blue-500' 
                  : 'text-gray-700 hover:bg-gray-50'
              }`}
            >
              <div className="flex items-center">
                <svg className="mr-3 h-5 w-5 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-5 5v-5zM11 19H6a2 2 0 01-2-2V7a2 2 0 012-2h5m5 0v6m0 0l3-3m-3 3l-3-3" />
                </svg>
                Notifications
              </div>
              {unreadNotificationCount > 0 && (
                <span className="inline-flex items-center justify-center px-2 py-1 text-xs font-bold leading-none text-white bg-red-600 rounded-full">
                  {unreadNotificationCount}
                </span>
              )}
            </button>

            {isAdmin() && (
              <button 
                onClick={() => navigate('/account-management')}
                className={`flex items-center px-6 py-2 text-sm w-full text-left ${
                  isActive('/account-management')
                    ? 'text-gray-900 bg-gray-100 border-r-2 border-blue-500' 
                    : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                <svg className="mr-3 h-5 w-5 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
                </svg>
                Account Management
              </button>
            )}
          </div>
        </nav>

        {/* User Profile Section at Bottom */}
        <div className="absolute bottom-0 w-64 p-4 border-t border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="flex-shrink-0">
              <img
                src={currentUser?.photoURL || `https://ui-avatars.com/api/?name=${encodeURIComponent(currentUser?.displayName || currentUser?.email || 'User')}&background=e5e7eb&color=374151`}
                alt={currentUser?.displayName || currentUser?.email || 'User'}
                className="w-8 h-8 rounded-full"
                onError={(e) => {
                  e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(currentUser?.displayName || currentUser?.email || 'User')}&background=e5e7eb&color=374151`;
                }}
              />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900 truncate">
                {currentUser?.displayName || 'User'}
              </p>
              <p className="text-xs text-gray-500 truncate">
                {currentUser?.email}
              </p>
            </div>
            <button
              onClick={handleLogout}
              className="flex-shrink-0 p-1 text-gray-400 hover:text-gray-600"
              title="Logout"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="ml-64 flex-1">
        {children}
      </div>

      {/* Logout Confirmation Modal */}
      {showLogoutConfirm && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3 text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
                <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 18.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <h3 className="text-lg leading-6 font-medium text-gray-900 mt-4">Confirm Logout</h3>
              <div className="mt-2 px-7 py-3">
                <p className="text-sm text-gray-500">
                  Are you sure you want to logout? You will need to sign in again to access your projects.
                </p>
              </div>
              <div className="items-center px-4 py-3">
                <button
                  onClick={confirmLogout}
                  className="px-4 py-2 bg-red-500 text-white text-base font-medium rounded-md w-24 mr-2 hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-red-300"
                >
                  Logout
                </button>
                <button
                  onClick={cancelLogout}
                  className="px-4 py-2 bg-gray-500 text-white text-base font-medium rounded-md w-24 hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-300"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Layout;
